import { Coordinate, TileSize } from "game-common/models"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { companionFloorOffset } from "game-common/room/ember/two_storey_house"
import { ScalingParams } from "../../../client_util"

export class TranslationComputer {
    static readonly PAN_MS = 360
    static readonly FLOOR_PAN_MS = 600
    static readonly JUMP_DISTANCE = TileSize
    tilemapMetaProvider: TileMapMetaProvider
    modifierX: number
    modifierY: number
    hasRunOnce: boolean = false
    ts: number
    noDelay: boolean = false
    xStatus: "left" | "center" | "right" | undefined
    yStatus: "top" | "center" | "bottom" | undefined
    private target?: Coordinate
    private panOffset?: Coordinate
    private panStartedAt = 0
    private floorPan = false
    private levelChanged = false

    update = (tilemapMetaProvider: TileMapMetaProvider) => {
        const previous = this.tilemapMetaProvider
        const previousHouse = previous?.twoStoreyHouse
        const nextHouse = tilemapMetaProvider.twoStoreyHouse
        const preserve = previous?.roomId === tilemapMetaProvider.roomId ||
            (previousHouse && nextHouse && previousHouse.groundRoomId === nextHouse.groundRoomId)
        if (preserve && this.hasRunOnce && previousHouse && nextHouse && previousHouse.floor !== nextHouse.floor) {
            // The destination used to be a companion drawn at this vertical offset.
            // Preserve its screen placement before easing toward the new active floor.
            this.modifierY += companionFloorOffset(previousHouse, nextHouse)
            this.levelChanged = true
        }
        this.tilemapMetaProvider = tilemapMetaProvider
        this.ts = Date.now()
        if (!preserve) this.reset()
    }

    reset = () => {
        this.hasRunOnce = false
        this.target = undefined
        this.panOffset = undefined
        this.floorPan = false
        this.levelChanged = false
        this.modifierX = undefined
        this.modifierY = undefined
        this.xStatus = undefined
        this.yStatus = undefined
    }

    ready = () => Date.now() - this.ts >= 150

    getCameraCenter = (): Coordinate | undefined => this.hasRunOnce
        ? { x: 50 - this.modifierX, y: 50 - this.modifierY } : undefined

    compute = (location: Coordinate, force?: boolean, fallOffsetY?: number) => {
        if (!this.hasRunOnce && !force && !this.noDelay && !this.ready()) return
        const X_FACTOR = ScalingParams.viewportX
        const Y_FACTOR = ScalingParams.viewportY - 1
        const minX = TileSize * X_FACTOR
        const maxX = (this.tilemapMetaProvider.width() - X_FACTOR) * TileSize
        const minY = TileSize * Y_FACTOR
        const maxY = (this.tilemapMetaProvider.height() - Y_FACTOR) * TileSize
        const target = {
            x: -Math.min(Math.max(location.x, minX), maxX) + 50,
            y: -Math.min(Math.max(location.y, minY), maxY) + 50,
        }
        const now = Date.now()
        if (fallOffsetY !== undefined) {
            // Follow the same physical height as the body. Independent per-floor pans
            // would decelerate and restart while the fall itself is accelerating.
            target.y -= fallOffsetY
            this.panOffset = undefined
            this.floorPan = false
            this.levelChanged = false
        } else if (this.levelChanged || (this.target && Math.hypot(target.x - this.target.x, target.y - this.target.y) >= TranslationComputer.JUMP_DISTANCE)) {
            // Retarget from the displayed position, including an unfinished pan.
            this.panOffset = { x: this.modifierX - target.x, y: this.modifierY - target.y }
            this.panStartedAt = now
            this.floorPan = this.levelChanged
            this.levelChanged = false
        }
        const duration = this.floorPan ? TranslationComputer.FLOOR_PAN_MS : TranslationComputer.PAN_MS
        const progress = Math.min(1, (now - this.panStartedAt) / duration)
        // Floor changes start and finish at rest, so the height adjustment doesn't
        // kick the camera at full speed as soon as the destination arrives.
        const remaining = !this.panOffset ? 0 : this.floorPan
            ? 1 - progress * progress * (3 - 2 * progress)
            : (1 - progress) ** 3
        this.modifierX = target.x + (this.panOffset?.x ?? 0) * remaining
        this.modifierY = target.y + (this.panOffset?.y ?? 0) * remaining
        if (progress >= 1) this.panOffset = undefined
        this.target = target
        this.hasRunOnce = true
        this.xStatus = location.x <= minX ? "left" : location.x >= maxX ? "right" : "center"
        this.yStatus = location.y <= minY ? "top" : location.y >= maxY ? "bottom" : "center"
    }
}
